import React, { FC, useMemo, useState } from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import SearchInput from "../../../components/form/SearchInput";
import FilterByButton from "../../ui/FilterButton";
import DateRangeSelector from "../../ui/DateRangeSelector";
import {
  UseUserFiltersReturn,
  UserListType,
  UserSortBy,
  UserSortOrder,
} from "./useUserFilters";

const SORT_BY_OPTIONS: { label: string; value: UserSortBy | null }[] = [
  { label: "Default", value: null },
  { label: "Registered date", value: "registeredDate" },
  { label: "Updated date", value: "updatedAt" },
  { label: "Order amount", value: "orderAmount" },
  { label: "Order count", value: "orderCount" },
  { label: "Product count", value: "productCount" },
];

const SORT_ORDER_OPTIONS: { label: string; value: UserSortOrder | null }[] = [
  { label: "Default", value: null },
  { label: "Ascending", value: "ASC" },
  { label: "Descending", value: "DESC" },
];

const FilterField: FC<{
  label: string;
  htmlFor?: string;
  active?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ label, htmlFor, active = false, children, className = "" }) => (
  <div className={`flex flex-col gap-1 w-full ${className}`}>
    <label
      htmlFor={htmlFor}
      className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide"
    >
      {label}
    </label>
    <div className={`rounded-lg ${active ? "ring-2 ring-primary/40" : ""}`}>
      {children}
    </div>
  </div>
);

const RangeNumberInput: FC<{
  id: string;
  name: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}> = ({ id, name, value, placeholder, onChange }) => (
  <input
    id={id}
    name={name}
    type="number"
    min={0}
    inputMode="numeric"
    autoComplete="off"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-[#E2E2E2] rounded-lg p-2 text-sm h-10 focus:outline-none focus:ring-1 focus:ring-primary bg-white"
  />
);

interface UserFilterBarProps {
  filters: UseUserFiltersReturn;
  userType: UserListType;
  panelId?: string;
  actions?: React.ReactNode;
  searchPlaceholder?: string;
}

const UserFilterBar: FC<UserFilterBarProps> = ({
  filters,
  userType,
  panelId = "user-filter-panel",
  actions,
  searchPlaceholder = "Search name, email, username",
}) => {
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 640px)").matches,
  );
  const showOrderFilters = userType !== "ADMIN";
  const showProductCount = userType === "VENDOR";

  const {
    inputValue,
    setInputValue,
    search,
    createdRange,
    setCreatedRange,
    minOrderAmountInput,
    setMinOrderAmountInput,
    maxOrderAmountInput,
    setMaxOrderAmountInput,
    minOrderCountInput,
    setMinOrderCountInput,
    maxOrderCountInput,
    setMaxOrderCountInput,
    minProductCountInput,
    setMinProductCountInput,
    maxProductCountInput,
    setMaxProductCountInput,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
  } = filters;

  const sortByOptions = useMemo(
    () =>
      showProductCount
        ? SORT_BY_OPTIONS
        : SORT_BY_OPTIONS.filter((option) => option.value !== "productCount"),
    [showProductCount],
  );

  const isCreatedRangeActive = !!(
    createdRange.startDate || createdRange.endDate
  );
  const prefix = userType.toLowerCase();

  return (
    <div className="mb-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
            aria-expanded={filtersOpen}
            aria-controls={panelId}
          >
            <SlidersHorizontal className="w-4 h-4 text-gray-500" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-lightPurple text-accent text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                filtersOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 hover:underline"
            >
              <X className="w-3.5 h-3.5" />
              Clear filters
            </button>
          )}
        </div>
        {actions ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>

      <div id={panelId} className={`mt-4 ${filtersOpen ? "" : "hidden"}`}>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <FilterField
            label="Search"
            htmlFor={`${prefix}-filter-search`}
            active={!!search}
            className="w-full sm:w-[260px]"
          >
            <SearchInput
              id={`${prefix}-filter-search`}
              name={`${prefix}Search`}
              autoComplete="off"
              wrapperClassName=""
              placeholder={searchPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </FilterField>

          <FilterField
            label="Registered date"
            htmlFor={`${prefix}-filter-created`}
            active={isCreatedRangeActive}
            className="w-full sm:w-[260px]"
          >
            <DateRangeSelector
              key={`created-${filterResetKey}`}
              id={`${prefix}-filter-created`}
              name={`${prefix}CreatedRange`}
              disableFuture
              className="w-full"
              placeholder="Registered from – to"
              initialRange={createdRange}
              onChange={(range) => setCreatedRange(range)}
            />
          </FilterField>

          {showOrderFilters && (
            <FilterField
              label="Order amount"
              htmlFor={`${prefix}-min-order-amount`}
              active={!!(minOrderAmountInput || maxOrderAmountInput)}
              className="w-full sm:w-[240px]"
            >
              <div className="flex items-center gap-2">
                <RangeNumberInput
                  id={`${prefix}-min-order-amount`}
                  name={`${prefix}MinOrderAmount`}
                  value={minOrderAmountInput}
                  placeholder="Min"
                  onChange={setMinOrderAmountInput}
                />
                <span className="text-gray-400 text-xs">–</span>
                <RangeNumberInput
                  id={`${prefix}-max-order-amount`}
                  name={`${prefix}MaxOrderAmount`}
                  value={maxOrderAmountInput}
                  placeholder="Max"
                  onChange={setMaxOrderAmountInput}
                />
              </div>
            </FilterField>
          )}

          {showOrderFilters && (
            <FilterField
              label="Order count"
              htmlFor={`${prefix}-min-order-count`}
              active={!!(minOrderCountInput || maxOrderCountInput)}
              className="w-full sm:w-[240px]"
            >
              <div className="flex items-center gap-2">
                <RangeNumberInput
                  id={`${prefix}-min-order-count`}
                  name={`${prefix}MinOrderCount`}
                  value={minOrderCountInput}
                  placeholder="Min"
                  onChange={setMinOrderCountInput}
                />
                <span className="text-gray-400 text-xs">–</span>
                <RangeNumberInput
                  id={`${prefix}-max-order-count`}
                  name={`${prefix}MaxOrderCount`}
                  value={maxOrderCountInput}
                  placeholder="Max"
                  onChange={setMaxOrderCountInput}
                />
              </div>
            </FilterField>
          )}

          {showProductCount && (
            <FilterField
              label="Product count"
              htmlFor={`${prefix}-min-product-count`}
              active={!!(minProductCountInput || maxProductCountInput)}
              className="w-full sm:w-[240px]"
            >
              <div className="flex items-center gap-2">
                <RangeNumberInput
                  id={`${prefix}-min-product-count`}
                  name={`${prefix}MinProductCount`}
                  value={minProductCountInput}
                  placeholder="Min"
                  onChange={setMinProductCountInput}
                />
                <span className="text-gray-400 text-xs">–</span>
                <RangeNumberInput
                  id={`${prefix}-max-product-count`}
                  name={`${prefix}MaxProductCount`}
                  value={maxProductCountInput}
                  placeholder="Max"
                  onChange={setMaxProductCountInput}
                />
              </div>
            </FilterField>
          )}

          <FilterField
            label="Sort by"
            htmlFor={`${prefix}-filter-sort-by`}
            active={!!sortBy}
            className="w-full sm:w-[200px]"
          >
            <FilterByButton
              key={`sort-by-${filterResetKey}`}
              id={`${prefix}-filter-sort-by`}
              name={`${prefix}SortBy`}
              fullWidth
              defaultValue={sortBy}
              buttonLabel="Default"
              options={sortByOptions}
              onSelect={(value: UserSortBy | null) => setSortBy(value)}
            />
          </FilterField>

          <FilterField
            label="Sort order"
            htmlFor={`${prefix}-filter-sort-order`}
            active={!!sortOrder}
            className="w-full sm:w-[180px]"
          >
            <FilterByButton
              key={`sort-order-${filterResetKey}`}
              id={`${prefix}-filter-sort-order`}
              name={`${prefix}SortOrder`}
              fullWidth
              defaultValue={sortOrder}
              buttonLabel="Default"
              options={SORT_ORDER_OPTIONS}
              onSelect={(value: UserSortOrder | null) => setSortOrder(value)}
            />
          </FilterField>
        </div>
      </div>
    </div>
  );
};

export default UserFilterBar;
